<template>
    <div>
        <redesign-base-card>
            <template #content>
                <div class="voicebot-card">
                    <div class="voicebot-card__header">
                        <div class="flex items-center gap-2 w-1/2">
                            <RedesignBaseIcon
                                name="Bot"
                                :size="32"
                                class="text-gray-550 flex-shrink-0"
                            />
                            <h2 class="text-sm font-medium text-gray-800 truncate">
                                {{ bot.VoicebotName }}
                            </h2>
                        </div>
                        <div class="flex">
                            <div class="flex items-center gap-2 text-xs text-gray-600">
                                <redesign-tag
                                    outline
                                    background-color="white"
                                    :tag-content="[{text: `${botCalls?.length}` || '', iconName: 'Phone'}]"
                                />
                                <redesign-tag
                                    outline
                                    :tag-content="[{text: averageDuration, iconName: 'Gauge'}]"
                                    background-color="white"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="voicebot-card__body px-4">
                        <div
                            v-for="(call) in botCalls"
                            :key="`${call.IvrUniqueID}_${widgetId}`"
                            class="text-sm border-b border-gray-100 py-1.5 last:border-0"
                        >
                            <VoicebotCardCallInfo
                                :bot-id="bot.VoicebotID"
                                :call="call"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </redesign-base-card>
    </div>
</template>

<script setup lang="ts">
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseCard from '@/components/RedesignBaseComponents/RedesignBaseCard.vue'
import VoicebotCardCallInfo from '@/components/RedesignComponents/Cards/Voicebots/VoicebotCardCallInfo.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { timeFormatter } from '@/helpers/timeFormatter'
import { Voicebot } from '@voicenter-team/events-sdk'

const props = defineProps<{
    bot: Voicebot,
    widgetId: number
}>()

let intervalId: ReturnType<typeof setInterval> | null = null

const tick = ref(Math.floor(Date.now()))

const botCalls = computed(() => {
    return props.bot?.Calls || []
})

const averageDuration = computed(() => {
    const calls = props.bot?.Calls || []
    if (!calls.length) return '00:00'

    const total = calls.reduce((sum, call) => {
        const duration = Math.max(0, tick.value - call.callStarted_UTC_CLIENT)
        return sum + duration
    }, 0)

    return timeFormatter(Math.floor(total / calls.length / 1000)) || '00:00'
})

onMounted(() => {
    intervalId = setInterval(() => {
        tick.value = Date.now()
    }, 1000)
})
onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
    }
})
</script>


<style lang="scss" scoped>
.voicebot-card {
    @apply flex flex-col;
    width: 451px;

    &__header {
        @apply flex items-center justify-between px-4 py-3;
        background-color: color-mix(in srgb, var(--primary-actions) 5%, transparent);
    }

    &__body {
        height: 152px;
        overflow-y: auto;
        background-color: var(--mainBgColor-color);
    }
}
</style>
